import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';
import Auth from './components/Auth';
import Tasks from './components/Tasks';
import SeedsPage from './components/SeedsPage';
import PlantsPage from './components/PlantsPage';
import Settings from './components/Settings';
import Account from './components/Account';
import Logbook from './components/Logbook';
import GardenPlanPage from './components/GardenPlanPage';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';
import Onboarding from './components/Onboarding';
import RootRedirect from './components/RootRedirect';
import AnalyticsTracker from './components/AnalyticsTracker';

import { UploadProvider } from './contexts/UploadContext';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

const sansSerifFont = [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
].join(',');

const theme = createTheme({
    palette: {
        primary: {
            main: '#2e7d32',
        },
        secondary: {
            main: '#ff9800',
        },
    },
    typography: {
        fontFamily: sansSerifFont,
    }
});


const App: React.FC = () => {
    const { i18n } = useTranslation();

    React.useEffect(() => {
        let unsubscribeProfile: () => void;

        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                // Listen to profile changes in real-time, which will sync across tabs
                unsubscribeProfile = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
                    if (docSnap.exists()) {
                        const profileData = docSnap.data();
                        if (profileData.language && profileData.language !== i18n.language) {
                            i18n.changeLanguage(profileData.language);
                        }
                    }
                });
            } else {
                if (unsubscribeProfile) {
                    unsubscribeProfile();
                }
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeProfile) unsubscribeProfile();
        };
    }, [i18n]);

    return (
        <UploadProvider>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Router>
                    <AnalyticsTracker />
                    <div className="App">
                        <Routes>
                            <Route path="/login" element={<Auth />} />
                            <Route
                                path="/"
                                element={<RootRedirect />}
                            />
                            <Route
                                path="/tasks"
                                element={<ProtectedRoute><MainLayout><Tasks /></MainLayout></ProtectedRoute>}
                            />
                            <Route
                                path="/seeds"
                                element={<ProtectedRoute><MainLayout><SeedsPage /></MainLayout></ProtectedRoute>}
                            />
                            <Route
                                path="/plants"
                                element={<ProtectedRoute><MainLayout><PlantsPage /></MainLayout></ProtectedRoute>}
                            />
                            <Route
                                path="/plan"
                                element={<ProtectedRoute><MainLayout><GardenPlanPage /></MainLayout></ProtectedRoute>}
                            />

                            <Route
                                path="/settings"
                                element={<ProtectedRoute><MainLayout><Settings /></MainLayout></ProtectedRoute>}
                            />
                            <Route
                                path="/account"
                                element={<ProtectedRoute><MainLayout><Account /></MainLayout></ProtectedRoute>}
                            />
                            <Route
                                path="/logbook"
                                element={<ProtectedRoute><MainLayout><Logbook /></MainLayout></ProtectedRoute>}
                            />
                            <Route
                                path="/onboarding"
                                element={<ProtectedRoute><Onboarding /></ProtectedRoute>}
                            />
                            <Route
                                path="/onboarding/spaces"
                                element={<ProtectedRoute><Onboarding /></ProtectedRoute>}
                            />
                            <Route
                                path="/onboarding/seeds"
                                element={<ProtectedRoute><Onboarding /></ProtectedRoute>}
                            />

                        </Routes>
                    </div>
                </Router>
            </ThemeProvider>
        </UploadProvider>
    );
}

export default App;
